import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Alert,
  Skeleton,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { empleadosApi } from "src/services/empleadosApi";
import ResumenTareasEmpleado from "src/components/dashboard-empleado/ResumenTareasEmpleado";
import ResumenPropuestasEmpleado from "src/components/dashboard-empleado/ResumenPropuestasEmpleado";
import { sucursalesApi } from "src/services/sucursalesApi";
import dayjs from "dayjs";
import DateRangeFilter from "src/components/estadisticas/date-range-filter";
import SucursalFilter from "src/components/estadisticas/sucursal-filter";
import ProductividadEmpleado from "src/components/dashboard-empleado/ProductividadEmpleado";
import RendimientoEmpleado from "src/components/dashboard-empleado/RendimientoEmpleado";
import { Dashboard as DashboardIcon } from "@material-ui/icons";
import ActividadSemanalEmpleado from "src/components/dashboard-empleado/ActividadSemanalEmpleado";
import ComparativaEquipoEmpleado from "src/components/dashboard-empleado/ComparativaEquipoEmpleado";
import TiempoPorClienteEmpleado from "src/components/dashboard-empleado/TiempoPorClienteEmpleado";

// Storage keys para persistir filtros
const getStorageKey = (rut) => ({
  sucursales: `dashboard_empleado_sucursales_${rut}`,
  fechaInicio: `dashboard_empleado_fecha_inicio_${rut}`,
  fechaFin: `dashboard_empleado_fecha_fin_${rut}`,
});

export default function DashboardEmpleado() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    empleado: usuario,
    sucursal: { refSucursal: sucursalEmpleado },
  } = useSelector((state) => state.auth.user);

  const { data: sucursalesEmpleado, isFetching: obteniendoSucursalesEmpleado } =
    sucursalesApi.useGetSucursalesEmpleadosQuery(usuario.rut);

  // Storage keys
  const STORAGE_KEYS = useMemo(() => getStorageKey(usuario.rut), [usuario.rut]);

  // Función para cargar desde storage
  const cargarDesdeStorage = useCallback(() => {
    try {
      const sucursalesGuardadas = localStorage.getItem(STORAGE_KEYS.sucursales);
      const fechaInicioGuardada = localStorage.getItem(
        STORAGE_KEYS.fechaInicio,
      );
      const fechaFinGuardada = localStorage.getItem(STORAGE_KEYS.fechaFin);

      return {
        sucursales: sucursalesGuardadas
          ? JSON.parse(sucursalesGuardadas)
          : [sucursalEmpleado],
        fechaInicio: fechaInicioGuardada
          ? dayjs(fechaInicioGuardada).toDate()
          : dayjs().subtract(30, "days").toDate(),
        fechaFin: fechaFinGuardada
          ? dayjs(fechaFinGuardada).toDate()
          : dayjs().toDate(),
      };
    } catch (error) {
      console.error("Error al cargar preferencias:", error);
      return {
        sucursales: [sucursalEmpleado],
        fechaInicio: dayjs().subtract(30, "days").toDate(),
        fechaFin: dayjs().toDate(),
      };
    }
  }, [STORAGE_KEYS, sucursalEmpleado]);

  // Estados
  const [filtrosInicializados, setFiltrosInicializados] = useState(false);
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(
    dayjs().subtract(30, "days").toDate(),
  );
  const [fechaFin, setFechaFin] = useState(dayjs().toDate());

  // Inicializar filtros desde storage
  useEffect(() => {
    if (!filtrosInicializados) {
      const preferencias = cargarDesdeStorage();
      setSucursalesSeleccionadas(preferencias.sucursales);
      setFechaInicio(preferencias.fechaInicio);
      setFechaFin(preferencias.fechaFin);
      setFiltrosInicializados(true);
    }
  }, [filtrosInicializados, cargarDesdeStorage]);

  // Función para guardar en storage
  const guardarEnStorage = useCallback(() => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.sucursales,
        JSON.stringify(sucursalesSeleccionadas),
      );
      localStorage.setItem(
        STORAGE_KEYS.fechaInicio,
        dayjs(fechaInicio).format("YYYY-MM-DD"),
      );
      localStorage.setItem(
        STORAGE_KEYS.fechaFin,
        dayjs(fechaFin).format("YYYY-MM-DD"),
      );
    } catch (error) {
      console.error("Error al guardar preferencias:", error);
    }
  }, [STORAGE_KEYS, sucursalesSeleccionadas, fechaInicio, fechaFin]);

  // Handlers de filtros
  const handleSetSucursalesSeleccionadas = useCallback((sucursales) => {
    setSucursalesSeleccionadas(sucursales);
  }, []);

  const handleSetFechaInicio = useCallback((fecha) => {
    setFechaInicio(fecha);
  }, []);

  const handleSetFechaFin = useCallback((fecha) => {
    setFechaFin(fecha);
  }, []);

  // Obtener sucursales para filtro
  const obtenerSucursalesParaFiltro = useCallback(() => {
    if (sucursalesSeleccionadas && sucursalesSeleccionadas.length > 0) {
      return sucursalesSeleccionadas;
    }
    // Si no hay seleccionadas Y tenemos datos de sucursales, usar todas
    if (sucursalesEmpleado && sucursalesEmpleado.length > 0) {
      return sucursalesEmpleado.map((s) => s.refSucursal);
    }
    return [];
  }, [sucursalesSeleccionadas, sucursalesEmpleado]);

  // Guardar preferencias cuando cambian los filtros
  useEffect(() => {
    if (filtrosInicializados) {
      guardarEnStorage();
    }
  }, [guardarEnStorage, filtrosInicializados]);

  // Queries para los datos
  const { data: dataTareas, isLoading: cargandoTareas } =
    empleadosApi.useGetResumenTareasQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataPropuestas, isLoading: cargandoPropuestas } =
    empleadosApi.useGetResumenPropuestasQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataProductividad, isFetching: cargandoProductividad } =
    empleadosApi.useGetProductividadEmpleadoQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM/DD"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM/DD"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataRendimiento, isFetching: cargandoRendimiento } =
    empleadosApi.useGetRendimientoEmpleadoQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM/DD"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM/DD"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataActividad, isFetching: cargandoActividad } =
    empleadosApi.useGetActividadSemanalEmpleadoQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM/DD"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM/DD"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataComparativa, isFetching: cargandoComparativa } =
    empleadosApi.useGetComparativaEquipoEmpleadoQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM/DD"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM/DD"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  const { data: dataTiempoCliente, isFetching: cargandoTiempoCliente } =
    empleadosApi.useGetTiempoPorClienteEmpleadoQuery(
      {
        rutEmpleado: usuario.rut,
        fechaInicio: dayjs(fechaInicio).format("YYYY/MM/DD"),
        fechaFin: dayjs(fechaFin).format("YYYY/MM/DD"),
        sucursales: obtenerSucursalesParaFiltro(),
      },
      { skip: !filtrosInicializados },
    );

  // Validar si hay errores en las queries
  const hayErrores = !dataTareas && !cargandoTareas && filtrosInicializados;

  // Skeleton para carga inicial
  if (!filtrosInicializados) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: { xs: 3, md: 5 } }}>
          <Skeleton variant="text" width={200} height={48} sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={400} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={400} />
            </Grid>
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Fade in timeout={400}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: { xs: 3, md: 4 },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
              }}
            >
              <DashboardIcon
                sx={{
                  fontSize: { xs: 32, md: 40 },
                  color: "primary.main",
                }}
              />
              <Box>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  fontWeight="bold"
                  color="text.primary"
                >
                  Mi Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bienvenido, {usuario.nombre || "Usuario"}
                </Typography>
              </Box>
            </Box>

            {/* Indicador de rango de fechas */}
            {!isMobile && (
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: "primary.lighter",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Período seleccionado
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="primary.main"
                >
                  {dayjs(fechaInicio).format("DD/MM/YYYY")} -{" "}
                  {dayjs(fechaFin).format("DD/MM/YYYY")}
                </Typography>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* Filtros */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: { xs: 3, md: 4 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Filtros
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DateRangeFilter
                  id="filtro-rango-fechas-dashboard"
                  label="Filtrar por rango de fechas"
                  fechaInicio={fechaInicio}
                  fechaFin={fechaFin}
                  onChangeFechaInicio={handleSetFechaInicio}
                  onChangeFechaFin={handleSetFechaFin}
                  maxDate={dayjs().toDate()}
                  tamanio={12}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SucursalFilter
                  id="filtro-sucursales-dashboard"
                  label="Filtrar por sucursales"
                  rutEmpleado={usuario.rut}
                  sucursalesSeleccionadas={sucursalesSeleccionadas}
                  onChangeSucursales={handleSetSucursalesSeleccionadas}
                  tamanio={12}
                />
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Mensaje de error si hay problemas */}
        {hayErrores && (
          <Fade in timeout={800}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              No se pudieron cargar algunos datos. Por favor, intenta
              nuevamente.
            </Alert>
          </Fade>
        )}

        {/* Sección de Resúmenes */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Resumen General
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Resumen de Tareas */}
            <Grid item xs={12}>
              <Fade in timeout={800}>
                <div>
                  <ResumenTareasEmpleado
                    data={dataTareas}
                    loading={cargandoTareas || obteniendoSucursalesEmpleado}
                  />
                </div>
              </Fade>
            </Grid>

            {/* Resumen de Propuestas */}
            <Grid item xs={12}>
              <Fade in timeout={1000}>
                <div>
                  <ResumenPropuestasEmpleado
                    data={dataPropuestas}
                    loading={cargandoPropuestas || obteniendoSucursalesEmpleado}
                  />
                </div>
              </Fade>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Análisis de Rendimiento
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Actividad Semanal */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1600}>
                <Box sx={{ height: "100%" }}>
                  <ActividadSemanalEmpleado
                    data={dataActividad}
                    loading={cargandoActividad || obteniendoSucursalesEmpleado}
                  />
                </Box>
              </Fade>
            </Grid>

            {/* Comparativa con Equipo */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1800}>
                <Box sx={{ height: "100%" }}>
                  <ComparativaEquipoEmpleado
                    data={dataComparativa}
                    loading={
                      cargandoComparativa || obteniendoSucursalesEmpleado
                    }
                  />
                </Box>
              </Fade>
            </Grid>

            {/* Tiempo por Cliente */}
            <Grid item xs={12}>
              <Fade in timeout={2000}>
                <div>
                  <TiempoPorClienteEmpleado
                    data={dataTiempoCliente}
                    loading={
                      cargandoTiempoCliente || obteniendoSucursalesEmpleado
                    }
                  />
                </div>
              </Fade>
            </Grid>
          </Grid>
        </Box>

        {/* Sección de Métricas Avanzadas */}
        <Box>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Métricas de Desempeño
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Productividad */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1200}>
                <Box sx={{ height: "100%" }}>
                  <ProductividadEmpleado
                    data={dataProductividad}
                    loading={
                      cargandoProductividad || obteniendoSucursalesEmpleado
                    }
                  />
                </Box>
              </Fade>
            </Grid>

            {/* Rendimiento */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1400}>
                <Box sx={{ height: "100%" }}>
                  <RendimientoEmpleado
                    data={dataRendimiento}
                    loading={
                      cargandoRendimiento || obteniendoSucursalesEmpleado
                    }
                  />
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Box>

        {/* Footer informativo */}
        {!isMobile && (
          <Fade in timeout={1600}>
            <Box
              sx={{
                mt: 4,
                p: 2,
                bgcolor: "background.neutral",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Los datos se actualizan en tiempo real. Última actualización:{" "}
                {dayjs().format("DD/MM/YYYY HH:mm")}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>
    </Container>
  );
}
